/**
 * The two things a conversation's header was missing.
 *
 * Searching inside this conversation, and quieting it for a while. Both are
 * per-conversation and both were only reachable by going somewhere else -
 * search did not exist at all, and muting was a whole-app setting or nothing.
 */

import { useState } from 'react';
import { cn } from '@/lib/cn';
import { openFinder } from '@/lib/finder';
import { MUTE_SPANS, mutedUntil, muteText, setMute } from '@/lib/mutes';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { MenuPanel, type MenuItem } from './Menu';
import { IconButton, useToast } from './ui';
import { Tip } from './HoverCard';

export function ChatTools({ channelId, name }: { channelId: string; name: string }) {
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const toast = useToast();
  /* Subscribed so the bell changes the moment the mute is set. */
  useSession((s) => s.settings?.mutes?.[channelId]);
  const until = mutedUntil(channelId);

  const items: MenuItem[] = until
    ? [{
        label: 'Unmute ' + name,
        icon: 'bell',
        onClick: () => { void setMute(channelId, 0); toast(name + ' is unmuted', 'good'); }
      }]
    : MUTE_SPANS.map((span) => ({
        label: span.label,
        icon: 'bellOff',
        onClick: () => {
          void setMute(channelId, span.minutes);
          toast('Quiet ' + (span.minutes > 60 * 24 * 365 ? 'until you turn it back on' : span.label.toLowerCase()), 'good');
        }
      }));

  return (
    <>
      <Tip text={'Find a message in ' + name}>
        <IconButton
          label={'Find a message in ' + name}
          onClick={() => openFinder({ start: 'search', channelId, channelName: name })}
        >
          <Icon name="search" size={17} />
        </IconButton>
      </Tip>

      <Tip text={until ? 'Muted ' + muteText(until) : 'Mute ' + name} sub={until ? 'Click to unmute' : undefined}>
        <IconButton
          label={until ? 'Muted, ' + muteText(until) + '. Change it.' : 'Mute ' + name}
          className={cn(until && 'text-accent')}
          onClick={(e) => {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setAt({ x: Math.max(8, r.right - 220), y: r.bottom + 4 });
          }}
        >
          <Icon name={until ? 'bellOff' : 'bell'} size={17} />
        </IconButton>
      </Tip>

      {at ? <MenuPanel at={at} items={items} onClose={() => setAt(null)} /> : null}
    </>
  );
}
